import { useUserStore } from '@/store/user';
import { defaultBatchSize } from './default';
import { useArtworkStore } from '@/store/artwork';
import type { Module, ModuleRoute } from '@/types/artwork/generate';
import { ModuleEnum } from '@/assets/artwork';
import { ckptTypeOptions } from '@/views/model/data';
import { getOptionLabel } from '@/utils/options';
import SvgIcon from '@/components/SvgIcon/index.vue';
import { h } from 'vue';

// 检测非会员是否使用了会员参数
export async function hasMemberPackageParam(module: Module): Promise<boolean> {
  const userStore = useUserStore();

  if (!userStore.showVip) {
    return false;
  }

  await userStore.queryUserRights();

  // 会员用户
  if (userStore.isMemberUser) {
    return false;
  }

  // 非会员用户
  const artworkStore = useArtworkStore();
  const params = artworkStore[ModuleEnum[module] as ModuleRoute]?.params ?? {};

  // 会员参数

  // 加速通道
  if (artworkStore.channel === 'expedite') {
    return true;
  }

  // 生图数量相关
  if (artworkStore.isFlux && params.batchSize > 1) {
    return true;
  }

  if (params?.batchSize > 2) {
    return true;
  }

  // 线稿提取、相似图裂变生图 2张是会员参数
  if ([ModuleEnum['sketch'], ModuleEnum['fission']].includes(params.module) && params?.batchSize > 1) {
    return true
  }

  // 高清修复和画面增强是独立会员功能
  if (params?.hdFix) {
    return true;
  }

  if (params?.enablePerturb) {
    return true;
  }

  // 语义增强引擎
  if (params?.alchemy) {
    return true;
  }

  return false;
}

// 非会员使用会员参数生图时，将会员可选参数重置为非会员可选参数
export function resetVipParams(module: Module) {
  const artworkStore = useArtworkStore();
  const userStore = useUserStore();
  const params = artworkStore[ModuleEnum[module] as ModuleRoute]?.params ?? {};

  artworkStore.channel = 'normal';

  if (
    params.batchSize > 2
    || (artworkStore.isFlux && params.batchSize > 1)
    || ([ModuleEnum['sketch'], ModuleEnum['fission']].includes(params.module) && params?.batchSize > 1)
  ) {
    params.batchSize = defaultBatchSize;
  }

  params.hdFix = false;
  params.enablePerturb = false;
  params.alchemy = false;

}

export function getMemberFunctionList(module: Module) {
  // 高清修复
  const hdFix = {
    label: '高清修复',
    content: '生成更高像素，细节更丰富的高质量图像',
    icon: h(SvgIcon, { directory: 'memberFunction', name: 'icon-HD', size: 16 }),
    activeIcon: h(SvgIcon, { directory: 'memberFunction', name: 'icon-HD-active', size: 16 }),
    image: new URL('@/assets/images/memberFunction/高清修复.png', import.meta.url).href,
  };

  // 画面增强
  const enablePerturb = {
    label: '画面增强',
    content: '「独家技术」增强生成画面，减少错乱繁杂元素',
    icon: h(SvgIcon, { directory: 'memberFunction', name: 'icon-image-enhancement', size: 16 }),
    activeIcon: h(SvgIcon, { directory: 'memberFunction', name: 'icon-image-enhancement-active', size: 16 }),
    image: new URL('@/assets/images/memberFunction/画面增强.png', import.meta.url).href,
  };

  // 语义增强引擎
  const alchemy = {
    label: '语义增强引擎',
    content: '「独家技术」增强模型语义理解能力，优化画面细节和精度',
    icon: h(SvgIcon, { directory: 'memberFunction', name: 'icon-semantic-enhancement', size: 16 }),
    activeIcon: h(SvgIcon, { directory: 'memberFunction', name: 'icon-semantic-enhancement-active', size: 16 }),
    image: new URL('@/assets/images/memberFunction/语义增强引擎.png', import.meta.url).href,
  };

  // 生图数量相关
  const batchSize4 = {
    label: '生图数量',
    content: '会员支持同时输出 4 张图',
    icon: h(SvgIcon, { directory: 'memberFunction', name: 'icon-image-number', size: 16 }),
    activeIcon: h(SvgIcon, { directory: 'memberFunction', name: 'icon-image-number-active', size: 16 }),
    image: new URL('@/assets/images/memberFunction/生图数量.png', import.meta.url).href,
  };

  const fluxBatchSize = {
    label: `生图数量`,
    content: `会员支持${getOptionLabel(ckptTypeOptions, 'Flux')}模型同时输出 2 张图`,
    icon: h(SvgIcon, { directory: 'memberFunction', name: 'icon-image-number', size: 16 }),
    activeIcon: h(SvgIcon, { directory: 'memberFunction', name: 'icon-image-number-active', size: 16 }),
    image: new URL('@/assets/images/memberFunction/生图数量.png', import.meta.url).href,
  }

  const speedChannel = {
    label: '加速通道',
    content: '会员插队加速，优先处理，减少排队时间，享受更畅快的生图体验',
    icon: h(SvgIcon, { directory: 'memberFunction', name: 'icon-speed', size: 16 }),
    activeIcon: h(SvgIcon, { directory: 'memberFunction', name: 'icon-speed-active', size: 16 }),
    image: new URL('@/assets/images/memberFunction/加速通道.png', import.meta.url).href,
  }

  // 非会员用户
  const artworkStore = useArtworkStore();
  const params = artworkStore[ModuleEnum[module] as ModuleRoute]?.params ?? {};

  const list = [];

  if (params.hdFix) {
    list.push(hdFix);
  }

  if (params.enablePerturb) {
    list.push(enablePerturb);
  }

  if (params.alchemy) {
    list.push(alchemy);
  }

  if (params.batchSize === 4) {
    list.push(batchSize4);
  } else if (artworkStore.isFlux && params.batchSize > 1) {
    list.push(fluxBatchSize);
  }

  if (artworkStore.channel === 'expedite') {
    list.push(speedChannel);
  }

  return list;
}